// Windowed rendering for a fixed-row-height list.
//
// The trace can run to thousands of events, and every row is a small SVG scene
// (band rects, lifeline segments, connector, hit targets). Mounting them all at
// once — and re-reconciling the lot on every selection or filter toggle — is
// what makes the view feel sluggish. This hook renders only the rows that
// intersect the viewport (plus an overscan margin) and hands back spacer heights
// that stand in for the rows above and below, so the scrollbar stays honest.
//
// It leans on one fact about the trace table: every row is exactly `rowHeight`
// tall (see ROW_H), so the visible slice is pure arithmetic on scrollTop — no
// per-row measurement needed.

import { type RefObject, useLayoutEffect, useState } from "react";

export interface VirtualRange {
  start: number; // first row index to render (inclusive)
  end: number; // one past the last row index to render (exclusive)
  padTop: number; // px spacer standing in for rows [0, start)
  padBottom: number; // px spacer standing in for rows [end, rowCount)
}

// Rendered on the very first paint, before the effect has measured the
// container. A viewport taller than this (~1300px of rows) would show a
// one-frame gap at the bottom, immediately corrected by the layout effect.
const INITIAL_ROWS = 60;

export function useVirtualRows(
  scrollRef: RefObject<HTMLElement | null>,
  rowCount: number,
  rowHeight: number,
  overscan = 8,
): VirtualRange {
  const [range, setRange] = useState<{ start: number; end: number }>(() => ({
    start: 0,
    end: Math.min(rowCount, INITIAL_ROWS),
  }));

  // useLayoutEffect (not useEffect) so the first real measurement lands before
  // the browser paints — the full list never reaches the screen.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    let frame = 0;
    const recompute = () => {
      frame = 0;
      const first = Math.floor(el.scrollTop / rowHeight);
      const visible = Math.ceil(el.clientHeight / rowHeight);
      const start = Math.max(0, first - overscan);
      const end = Math.min(rowCount, first + visible + overscan);
      setRange((prev) => (prev.start === start && prev.end === end ? prev : { start, end }));
    };
    // Coalesce bursts of scroll events into one recompute per frame. rAF runs
    // before paint, so a programmatic scrollTop change still updates the window
    // before that frame is drawn — no blank flash when jumping to a far row.
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(recompute);
    };

    recompute();
    el.addEventListener("scroll", onScroll, { passive: true });
    const ro = new ResizeObserver(recompute);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", onScroll);
      ro.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [scrollRef, rowCount, rowHeight, overscan]);

  // Clamp against the current rowCount: it may have shrunk since the last scroll
  // event (e.g. the user re-ran a shorter program) before the effect re-fires.
  const start = Math.min(range.start, rowCount);
  const end = Math.min(range.end, rowCount);
  return { start, end, padTop: start * rowHeight, padBottom: (rowCount - end) * rowHeight };
}
